import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blog`);
        if (!cancel) setPosts(data.posts || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const [feature, ...rest] = posts;

  return (
    <>
      <section className="relative pt-40 pb-12 md:pt-52 md:pb-16 border-b border-white/5" data-testid="blog-hero">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-6">
              <span className="text-white/35 mr-3">J / 00</span>The Journal
            </div>
            <h1 className="font-display text-6xl md:text-[8vw] leading-[0.92] tracking-[-0.03em] text-white">
              Field <span className="font-serif-display text-[var(--gold)]">notes</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-white/65 text-lg leading-relaxed">
              Long-form thoughts on light, gear, weddings, and the slow craft of building a portfolio you’re proud of.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          {loading && <div className="font-mono-tech text-white/40 text-sm" data-testid="blog-loading">Loading entries…</div>}

          {feature && (
            <Reveal>
              <Link
                to={`/blog/${feature.slug}`}
                className="group grid md:grid-cols-12 gap-8 md:gap-12 mb-20"
                data-testid={`blog-feature-${feature.slug}`}
              >
                <div className="md:col-span-7 relative aspect-[16/10] overflow-hidden bg-[#0a0a0a]">
                  <img src={feature.cover} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                </div>
                <div className="md:col-span-5 flex flex-col justify-end">
                  <div className="flex items-center gap-4 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/45 mb-4">
                    <span className="text-[var(--cyan)]">{feature.category}</span>
                    <span>{feature.read_time}</span>
                    <span>{feature.date}</span>
                  </div>
                  <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight leading-[1.05] group-hover:text-[var(--gold)] transition-colors">
                    {feature.title}
                  </h2>
                  <p className="mt-4 text-white/65 leading-relaxed">{feature.excerpt}</p>
                  <div className="mt-6 inline-flex items-center gap-2 font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)]">
                    Read entry <ArrowUpRight size={14} />
                  </div>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="grid md:grid-cols-3 gap-10">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link to={`/blog/${p.slug}`} className="group block" data-testid={`blog-card-${p.slug}`}>
                  <div className="relative aspect-[5/6] overflow-hidden bg-[#0a0a0a]">
                    <img src={p.cover} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                    <div className="absolute top-3 left-3 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white bg-black/55 px-2 py-1 border border-white/10">{p.category}</div>
                  </div>
                  <div className="mt-5 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/40">{p.date} · {p.read_time}</div>
                  <h3 className="mt-2 font-display text-2xl text-white tracking-tight group-hover:text-[var(--gold)] transition-colors">{p.title}</h3>
                  <p className="mt-2 text-sm text-white/55 leading-relaxed">{p.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Blog;
